import React, { useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CheckCircle2, FileText, Loader2, Sparkles, UploadCloud, UserPlus, XCircle, BookPlus } from "lucide-react";

const ResultRow = ({ r }) => (
  <li className="rounded-lg border border-slate-200 p-3 text-sm" data-testid={`ai-import-result-${r.file}`}>
    <div className="flex items-start gap-2">
      {r.status === "OK" ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-green-600" /> : <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />}
      <div className="min-w-0 flex-1">
        <div className="font-semibold text-slate-800">{r.status === "OK" ? `Turma ${r.class_name}` : r.file}</div>
        {r.status === "OK" ? (
          <div className="text-xs text-slate-500">{r.file} · {r.shift} · {r.slots} tempo(s){r.vacant ? ` · ${r.vacant} sem professor` : ""}{r.replaced ? " · quadro anterior substituído" : " · quadro novo"}</div>
        ) : <div className="text-xs text-red-600">{r.error}</div>}
        {r.warnings?.length > 0 && <ul className="mt-1 list-disc pl-4 text-xs text-amber-700">{r.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>}
        {r.periods && <div className="mt-1 text-xs text-green-700" data-testid="ai-import-periods">Horários do PDF aplicados: {r.periods.join(" · ")}</div>}
      </div>
    </div>
  </li>
);

const Summary = ({ job }) => {
  const ok = job.results.filter((r) => r.status === "OK");
  return (
    <div className="max-h-[60vh] space-y-3 overflow-y-auto" data-testid="ai-import-summary">
      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="rounded-lg bg-green-50 p-2"><div className="text-xl font-bold text-green-700" data-testid="ai-import-ok-count">{ok.length}</div><div className="text-xs text-green-700">turma(s) importada(s)</div></div>
        <div className="rounded-lg bg-blue-50 p-2"><div className="text-xl font-bold text-blue-700" data-testid="ai-import-teachers-count">{job.created_teachers.length}</div><div className="text-xs text-blue-700">professor(es) criado(s)</div></div>
        <div className="rounded-lg bg-violet-50 p-2"><div className="text-xl font-bold text-violet-700" data-testid="ai-import-subjects-count">{job.created_subjects.length}</div><div className="text-xs text-violet-700">disciplina(s) criada(s)</div></div>
      </div>
      <ul className="space-y-2">{job.results.map((r) => <ResultRow key={r.file} r={r} />)}</ul>
      {job.created_teachers.length > 0 && (
        <div data-testid="ai-import-created-teachers"><h4 className="mb-1 flex items-center gap-1.5 text-xs font-bold uppercase text-slate-600"><UserPlus className="h-4 w-4" />Professores adicionados ao cadastro</h4>
          <p className="text-sm text-slate-700">{job.created_teachers.map((t) => `${t.name}${t.registration ? ` (${t.registration})` : ""}`).join(" · ")}</p>
          <p className="mt-1 text-xs text-amber-700" data-testid="ai-import-no-email-note">Sem e-mail no PDF: o acesso de cada professor será criado e enviado assim que o e-mail for cadastrado em Professores.</p></div>
      )}
      {job.created_subjects.length > 0 && (
        <div data-testid="ai-import-created-subjects"><h4 className="mb-1 flex items-center gap-1.5 text-xs font-bold uppercase text-slate-600"><BookPlus className="h-4 w-4" />Disciplinas adicionadas</h4>
          <p className="text-sm text-slate-700">{job.created_subjects.map((s) => s.name).join(" · ")}</p></div>
      )}
    </div>
  );
};

export const ScheduleAiImport = ({ schoolId, onDone }) => {
  const [open, setOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const input = useRef(null);
  const reset = () => { setFiles([]); setJob(null); };
  const run = async () => {
    setBusy(true);
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    if (schoolId) fd.append("school_id", schoolId);
    try {
      const { data } = await api.post("/schedule/ai-import", fd, { timeout: 300000 });
      let j = { status: "PROCESSANDO", total: data.total, done: 0, results: [] };
      setJob(j);
      while (j.status !== "CONCLUIDO") {
        await new Promise((r) => setTimeout(r, 3000));
        j = (await api.get(`/schedule/ai-import/${data.id}`)).data;
        setJob(j);
      }
      toast.success("Importação concluída"); onDone?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setJob(null); } finally { setBusy(false); }
  };
  const done = job?.status === "CONCLUIDO";
  return (
    <>
      <Button onClick={() => { reset(); setOpen(true); }} className="bg-blue-700 hover:bg-blue-800" data-testid="schedule-ai-import-btn">
        <Sparkles className="mr-1.5 h-4 w-4" />Importar Quadro de Horários com IA
      </Button>
      <Dialog open={open} onOpenChange={(o) => !busy && setOpen(o)}>
        <DialogContent className="sm:max-w-xl" data-testid="schedule-ai-import-dialog">
          <DialogHeader><DialogTitle className="pr-6">Importar Quadro de Horários com IA</DialogTitle></DialogHeader>
          {done ? <Summary job={job} /> : job ? (
            <div className="flex flex-col items-center gap-3 py-8 text-center" data-testid="ai-import-progress">
              <Loader2 className="h-8 w-8 animate-spin text-blue-700" />
              <p className="text-sm text-slate-600">A IA está lendo os quadros... {job.done || 0} de {job.total} arquivo(s)</p>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-slate-500">Envie os PDFs do Conexão Educação (um por turma). A IA identifica a turma, os dias, os tempos, as disciplinas e os professores e implanta o quadro em cada turma. Quadros existentes são substituídos; professores e disciplinas que não estiverem cadastrados são adicionados.</p>
              <input ref={input} type="file" multiple hidden accept=".pdf,application/pdf" onChange={(e) => { setFiles([...e.target.files]); e.target.value = ""; }} data-testid="schedule-ai-file-input" />
              <button type="button" onClick={() => input.current?.click()} className="flex w-full flex-col items-center gap-2 rounded-xl border-2 border-dashed border-slate-300 p-6 text-sm text-slate-500 hover:border-blue-400 hover:bg-blue-50/40" data-testid="schedule-ai-pick-btn">
                <UploadCloud className="h-7 w-7 text-blue-700" />Selecionar PDFs (vários ao mesmo tempo)
              </button>
              {files.length > 0 && <ul className="max-h-40 space-y-1 overflow-y-auto text-sm" data-testid="schedule-ai-file-list">{files.map((f) => <li key={f.name} className="flex items-center gap-1.5 text-slate-700"><FileText className="h-4 w-4 text-slate-400" />{f.name}</li>)}</ul>}
            </div>
          )}
          <DialogFooter>
            {done ? <Button onClick={() => setOpen(false)} data-testid="ai-import-close">Fechar</Button> : (
              <Button onClick={run} disabled={busy || !files.length} className="bg-blue-700 hover:bg-blue-800" data-testid="schedule-ai-run-btn">
                {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1.5 h-4 w-4" />}Importar {files.length || ""} arquivo(s)
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
